/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "./CodeableConceptEntry";
import QuantityEntry, { displayStringFromFHIRQuantity } from "./QuantityEntry";
import RangeEntry, { displayStringFromFHIRRange } from "./RangeEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "./ReferenceEntry";
import CodingEntry, { CodingEntryWithSystemSelector, displayStringFromFHIRCoding } from "./CodingEntry";
import DatatypeSelector from "./DatatypeSelector";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const DisplayFromFHIRUsageContext = ({ usageContext }) => {
  if (usageContext) {
    if (Array.isArray(usageContext) || typeof usageContext !== "object") {
      return <>[ERROR: UsageContext data not interpretable.]</>;
    }
    let code = displayStringFromFHIRCoding(usageContext.code) || "[Usage Context Code Missing.]";
    if (usageContext.valueCodeableConcept) {
      return <>{code} matches {displayStringFromFHIRCodeableConcept(usageContext.valueCodeableConcept)}</>
    } else if (usageContext.valueQuantity) {
      return <>{code} matches {displayStringFromFHIRQuantity(usageContext.valueQuantity)}</>
    } else if (usageContext.valueRange) {
      return <>{code} matches {displayStringFromFHIRRange(usageContext.valueRange)}</>
    } else if (usageContext.valueReference) {
      return <>{code} matches <DisplayFromFHIRReference reference={usageContext.valueReference} /></>
    } else {
      return <>{code} matches [Usage Context Value Missing.]</>
    }
  } else {
    return <></>
  }
}

const updateResourceState = (elementName, setResourceState, usageContextState, valueDatatypeState) => {
  let newUsageContext = {};
  if (usageContextState.extension) { newUsageContext.extension = usageContextState.extension; }
  if (usageContextState.code && Object.keys(usageContextState.code).length) {
    newUsageContext.code = usageContextState.code;
  }
  if (valueDatatypeState === 'CodeableConcept' && usageContextState.valueCodeableConcept && Object.keys(usageContextState.valueCodeableConcept).length) {
    newUsageContext.valueCodeableConcept = usageContextState.valueCodeableConcept;
  }
  if (valueDatatypeState === 'Quantity' && usageContextState.valueQuantity && Object.keys(usageContextState.valueQuantity).length) {
    newUsageContext.valueQuantity = usageContextState.valueQuantity;
  }
  if (valueDatatypeState === 'Range' && usageContextState.valueRange && Object.keys(usageContextState.valueRange).length) {
    newUsageContext.valueRange = usageContextState.valueRange;
  }
  if (valueDatatypeState === 'Reference' && usageContextState.valueReference && Object.keys(usageContextState.valueReference).length) {
    newUsageContext.valueReference = usageContextState.valueReference;
  }
  if (Object.keys(newUsageContext).length === 0) {
    newUsageContext = null;
  }
  setResourceState(prevState => { return { ...prevState, [elementName]: newUsageContext } });
};

const usageContextDotCodeValueSet = [
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "gender", display: "Gender" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "age", display: "Age Range" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "focus", display: "Clinical Focus" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "user", display: "User Type" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "workflow", display: "Workflow Setting" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "task", display: "Workflow Task" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "venue", display: "Clinical Venue" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "species", display: "Species" },
  { system: "http://terminology.hl7.org/CodeSystem/usage-context-type", code: "program", display: "Program" },
  { system: "https://fevir.net/resources/CodeSystem/179423", code: "evidence-communication", display: "Evidence Communication" },
  { "system": "https://clinicaltrials.gov", "code": "interventionType", "display": "Intervention Type for ClinicalTrials.gov" },
  { "system": "https://clinicaltrials.gov", "code": "measureType", "display": "Measure Type" },
  { "system": "https://clinicaltrials.gov", "code": "outcomeMeasureType", "display": "Outcome Measure Type" },
  { "system": "https://clinicaltrials.gov", "code": "adverseEventsType", "display": "Adverse Events Outcome Type for ClinicalTrials.gov" },
  { "system": "https://clinicaltrials.gov", "code": "adverseEventsOrganSystem", "display": "Organ System Classification for Adverse Events" }
];
const usageContextDotValueAllowedDatatypes = ['CodeableConcept', 'Quantity', 'Range', 'Reference'];
const UsageContextEntry = ({ elementName, fieldLabel, inTableCell, startingValue, setResourceState,
  valueSet, valueSetForType, referencedResourceTypes, dataEntryStyle, debug, systemChoices, setSystemChoices,
  systemChoicesOpen, startCollapsed }) => {
  if (!valueSetForType) {
    valueSetForType = usageContextDotCodeValueSet;
  }
  if (!referencedResourceTypes) {
    referencedResourceTypes = ['PlanDefinition', 'ResearchStudy', 'InsurancePlan', 'HealthcareService', 'Group', 'Location', 'Organization'];
  }

  const [usageContextState, setUsageContextState] = useState({});
  const [valueDatatypeState, setValueDatatypeState] = useState('none');
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  useEffect(() => {
    let startingUsageContext = { "code": "", "valueCodeableConcept": "", "valueQuantity": "", "valueRange": "", "valueReference": "" };
    let startingValueDatatype = 'none';
    if (startingValue) {
      if (startingValue.extension) { startingUsageContext.extension = startingValue.extension; }
      if (startingValue.code) { startingUsageContext.code = startingValue.code; }
      if (startingValue.valueCodeableConcept) {
        startingUsageContext.valueCodeableConcept = startingValue.valueCodeableConcept;
        startingValueDatatype = 'CodeableConcept';
      }
      if (startingValue.valueQuantity) {
        startingUsageContext.valueQuantity = startingValue.valueQuantity;
        startingValueDatatype = 'Quantity';
      }
      if (startingValue.valueRange) {
        startingUsageContext.valueRange = startingValue.valueRange;
        startingValueDatatype = 'Range';
      }
      if (startingValue.valueReference) {
        startingUsageContext.valueReference = startingValue.valueReference;
        startingValueDatatype = 'Reference';
      }
    }
    setUsageContextState(JSON.parse(JSON.stringify(startingUsageContext)));
    setValueDatatypeState(startingValueDatatype);
  }, []);

  useEffect((() => {
    if (Object.keys(usageContextState).length) {
      updateResourceState(elementName, setResourceState, usageContextState, valueDatatypeState);
    }
  }), [usageContextState, valueDatatypeState]);

  if (startCollapsedState) {
    return <>
      <div>
        <b>{fieldLabel}: </b>
        {startingValue && <DisplayFromFHIRUsageContext usageContext={startingValue} />}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </div>
    </>
  } else if (Object.keys(usageContextState).length > 0) {
    return <>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        {systemChoices ?
          <CodingEntryWithSystemSelector elementName='code' fieldLabel={'Type of ' + fieldLabel}
            startingValue={usageContextState.code} setResourceState={setUsageContextState}
            systemChoices={systemChoices} setSystemChoices={setSystemChoices} systemChoicesOpen={systemChoicesOpen}
            debug={debug} />
          :
          <CodingEntry elementName='code' fieldLabel={'Type of ' + fieldLabel}
            valueSet={valueSetForType} dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
            startingValue={usageContextState.code} setResourceState={setUsageContextState} />
        }
        <p style={{ marginBottom: "0px" }}><b>Value:</b></p>
        <div style={{ marginLeft: "24px" }}>
          <DatatypeSelector elementXName='value[x]' allowedDatatypes={usageContextDotValueAllowedDatatypes}
            datatypeState={valueDatatypeState} setDatatypeState={setValueDatatypeState} />
          {valueDatatypeState === 'CodeableConcept' &&
            <CodeableConceptEntry elementName='valueCodeableConcept' fieldLabel={'Value for ' + fieldLabel}
              valueSet={valueSet} dataEntryStyle={dataEntryStyle}
              startingValue={usageContextState.valueCodeableConcept} setResourceState={setUsageContextState} />}
          {valueDatatypeState === 'Quantity' &&
            <QuantityEntry elementName='valueQuantity' fieldLabel={'Value for ' + fieldLabel}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell}
              startingValue={usageContextState.valueQuantity} setResourceState={setUsageContextState} />}
          {valueDatatypeState === 'Range' &&
            <RangeEntry elementName='valueRange' fieldLabel={'Value for ' + fieldLabel}
              dataEntryStyle={dataEntryStyle}
              startingValue={usageContextState.valueRange} setResourceState={setUsageContextState} />}
          {valueDatatypeState === 'Reference' &&
            <ReferenceEntry elementName='valueReference' fieldLabel={'Value for ' + fieldLabel}
              dataEntryStyle={dataEntryStyle} inTableCell={inTableCell} referencedResourceTypes={referencedResourceTypes}
              enableCreation={true}
              startingValue={usageContextState.valueReference} setResourceState={setUsageContextState} />}
        </div>
      </div>
    </>
  }
};

export default UsageContextEntry;
export { DisplayFromFHIRUsageContext };